import { getApiBase } from './config';
import { getAuthHeaders } from './auth';

const API_BASE = getApiBase();

function getHeaders(extra?: HeadersInit): HeadersInit {
  return {
    ...getAuthHeaders({ 'Content-Type': 'application/json' }),
    ...extra,
  };
}

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
  const url = path.startsWith('http')
    ? path
    : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  const r = await fetch(url, {
    ...options,
    credentials: 'omit',
    headers: { ...getHeaders(), ...options?.headers } as HeadersInit,
  });

  const text = await r.text();
  if (!r.ok) {
    if (r.status === 401 && typeof window !== 'undefined') {
      window.location.href = '/login';
      return new Promise(() => {});
    }
    let msg = `خطا: ${r.status}`;
    try {
      if (text) {
        const j = JSON.parse(text);
        msg = j.message ?? j.error ?? msg;
      }
    } catch {}
    throw new Error(msg);
  }
  if (!text?.trim()) return {} as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('پاسخ نامعتبر');
  }
}

export interface AnalysisSectionItem {
  id: number;
  name: string;
  unit: string | null;
  quantity: string;
  unit_cost: string;
  total_cost: string;
  description: string | null;
  order: number;
  notes: string | null;
}

export interface AiMissingItem {
  section?: string;
  name: string;
  description?: string;
  unit?: string;
  estimated_quantity?: string | number;
  estimated_unit_cost?: string | number;
  reason?: string;
}

export interface AiSuggestion {
  type?: 'add' | 'modify' | 'remove' | 'warning';
  target?: string;
  message: string;
  priority?: 'high' | 'medium' | 'low';
  index?: number;
  accepted_at?: string;
}

export interface AiReviewData {
  last_review?: {
    analysis?: { summary?: string; issues?: string[]; strengths?: string[] };
    missing_items?: AiMissingItem[];
    suggestions?: AiSuggestion[];
    reviewed_at?: string;
  };
  conversation?: Array<{
    role: 'user' | 'assistant';
    content: string;
    at?: string;
    type?: string;
    applied_actions?: Array<{ type?: string; label?: string }>;
  }>;
  accepted_suggestions?: AiSuggestion[];
  applied_missing_items?: Array<{ index: number; item_id: number; section_id: number; name: string; applied_at?: string }>;
}

export interface AnalysisSection {
  id: number;
  cost_analysis_id: number;
  name: string;
  description: string | null;
  order: number;
  base_cost: string;
  calculated_cost: string;
  insurance_percentage: string | null;
  notes: string | null;
  items?: AnalysisSectionItem[];
}

export interface AnalysisItem {
  id: number;
  project_id: number;
  name: string;
  description: string | null;
  initial_estimate: string | null;
  tax_percentage: string | null;
  insurance_mode?: 'global' | 'sections' | null;
  insurance_percentage: string | null;
  profit_percentage: string | null;
  profit_type: string | null;
  profit_amount: string | null;
  profit_amount_meaning?: 'before_adjustment' | 'with_adjustment' | null;
  total_base_cost: string | null;
  total_tax: string | null;
  total_insurance: string | null;
  total_profit: string | null;
  total_cost: string | null;
  currency: string | null;
  is_active: boolean;
  project?: { id: number; name: string; contract_number?: string; location?: string; initial_estimate?: string | null; client?: { id: number; name: string; logo?: string } };
  target_contractor_coefficient?: string | number | null;
  auto_calculate_coefficient?: boolean | null;
  has_prepayment?: boolean;
  prepayment_installments?: number | null;
  prepayment_total_percent?: string | number | null;
  prepayment_percentages?: number[] | null;
  has_adjustment?: boolean;
  adjustment_predicted_percentage?: string | number | null;
  has_maintenance_period?: boolean;
  maintenance_duration?: string | null;
  maintenance_retention_percentage?: string | number | null;
  extra_coefficients?: Array<{ name: string; percentage: number }> | null;
  contract_text?: string | null;
  ai_review_data?: AiReviewData | null;
  sections?: AnalysisSection[];
  sections_count?: number;
  creator?: { id: number; name: string };
  updater?: { id: number; name: string };
  created_at?: string;
  updated_at?: string;
}

export interface PrintData {
  cost_analysis: AnalysisItem;
  project: { name: string; contract_number: string; location: string; formatted_status: string; initial_estimate: string | null };
  client: { name: string; logo: string | null };
  company: { company_name: string; company_logo: string | null };
  effective_profit_percentage: number;
  initial_estimate: number;
  contractor_coefficient: number | null;
}

export interface ProjectOption {
  id: number;
  name: string;
  original_name: string;
  contract_number: string | null;
  status: string;
  initial_estimate: string | null;
  currency: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
}

export const projectAnalysisApi = {
  list: (params?: { project_id?: number; search?: string; is_active?: boolean; per_page?: number; page?: number }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', String(params.project_id));
    if (params?.search) q.set('search', params.search);
    if (params?.is_active !== undefined) q.set('is_active', String(params.is_active ? 1 : 0));
    if (params?.per_page) q.set('per_page', String(params.per_page));
    if (params?.page) q.set('page', String(params.page));
    const query = q.toString();
    return fetchApi<PaginatedResponse<AnalysisItem>>(`/analyses${query ? '?' + query : ''}`);
  },

  dashboardStats: () =>
    fetchApi<ApiResponse<{
      total: number;
      active_count: number;
      recent_count: number;
      loss_count?: number;
      portfolio_value?: number;
      portfolio_base?: number;
      portfolio_profit?: number;
      avg_coefficient?: number | null;
      coefficient_count?: number;
      recent_analyses?: Array<{
        id: number;
        name: string;
        project_id: number;
        total_cost: string | null;
        total_base_cost?: string | null;
        total_profit?: string | null;
        initial_estimate?: string | null;
        created_at: string;
        updated_at?: string;
        is_active: boolean;
        sections_count: number;
        project?: { id: number; name: string; contract_number?: string; initial_estimate?: string | null };
      }>;
    }>>('/dashboard-stats'),

  projects: () =>
    fetchApi<ApiResponse<ProjectOption[]>>('/projects'),

  projectsWithoutAnalysis: () =>
    fetchApi<ApiResponse<Array<{ id: number; name: string; contract_number?: string }>>>('/projects-without-analysis'),

  get: (id: number) =>
    fetchApi<ApiResponse<AnalysisItem>>(`/analyses/${id}`),

  printData: (id: number) =>
    fetchApi<ApiResponse<PrintData>>(`/analyses/${id}/print-data`),

  store: (data: {
    project_id: number;
    name: string;
    description?: string;
    initial_estimate?: number | null;
    tax_percentage?: number;
    insurance_mode?: 'global' | 'sections';
    insurance_percentage?: number;
    profit_percentage?: number;
    profit_type?: string;
    profit_amount?: number;
    currency?: string;
    is_active?: boolean;
    has_prepayment?: boolean;
    prepayment_installments?: number;
    prepayment_percentages?: number[];
    has_adjustment?: boolean;
    adjustment_predicted_percentage?: number;
    has_maintenance_period?: boolean;
    maintenance_duration?: string;
    maintenance_retention_percentage?: number;
    extra_coefficients?: Array<{ name: string; percentage: number }>;
  }) =>
    fetchApi<ApiResponse<AnalysisItem>>('/analyses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: number, data: Partial<{ project_id: number; name: string; description: string; initial_estimate: number | null; tax_percentage: number; profit_percentage: number; profit_type: string; profit_amount: number; profit_amount_meaning: string; currency: string; is_active: boolean; has_prepayment: boolean; prepayment_installments: number; prepayment_total_percent: number; prepayment_percentages: number[]; has_adjustment: boolean; adjustment_predicted_percentage: number; has_maintenance_period: boolean; maintenance_duration: string; maintenance_retention_percentage: number; extra_coefficients: Array<{ name: string; percentage: number }> }>) =>
    fetchApi<ApiResponse<AnalysisItem>>(`/analyses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  destroy: (id: number) =>
    fetchApi<{ success: boolean }>(`/analyses/${id}`, { method: 'DELETE' }),

  recalculate: (id: number) =>
    fetchApi<ApiResponse<AnalysisItem>>(`/analyses/${id}/recalculate`, { method: 'POST' }),

  updateSettings: (id: number, data: Record<string, unknown>) =>
    fetchApi<ApiResponse<AnalysisItem>>(`/analyses/${id}/settings`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  storeSection: (analysisId: number, data: { name: string; description?: string; insurance_percentage?: number }) =>
    fetchApi<ApiResponse<AnalysisSection>>(`/analyses/${analysisId}/sections`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSection: (analysisId: number, sectionId: number, data: Partial<{ name: string; description: string; insurance_percentage: number }>) =>
    fetchApi<ApiResponse<AnalysisSection>>(`/analyses/${analysisId}/sections/${sectionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  destroySection: (analysisId: number, sectionId: number) =>
    fetchApi<{ success: boolean }>(`/analyses/${analysisId}/sections/${sectionId}`, { method: 'DELETE' }),

  storeItem: (analysisId: number, sectionId: number, data: { name: string; unit?: string; quantity: number; unit_cost: number; description?: string }) =>
    fetchApi<ApiResponse<AnalysisSectionItem>>(`/analyses/${analysisId}/sections/${sectionId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateItem: (analysisId: number, sectionId: number, itemId: number, data: Partial<{ name: string; unit: string; quantity: number; unit_cost: number; description: string }>) =>
    fetchApi<ApiResponse<AnalysisSectionItem>>(`/analyses/${analysisId}/sections/${sectionId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  destroyItem: (analysisId: number, sectionId: number, itemId: number) =>
    fetchApi<{ success: boolean }>(`/analyses/${analysisId}/sections/${sectionId}/items/${itemId}`, { method: 'DELETE' }),

  copyToProject: (analysisId: number, data: { target_project_id: number; new_name?: string }) =>
    fetchApi<ApiResponse<AnalysisItem>>(`/analyses/${analysisId}/copy-to-project`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  copySections: (analysisId: number, data: { target_analysis_id: number; section_ids: number[] }) =>
    fetchApi<ApiResponse<AnalysisItem>>(`/analyses/${analysisId}/copy-sections`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  copyItems: (analysisId: number, sectionId: number, data: { target_section_id: number; item_ids: number[] }) =>
    fetchApi<ApiResponse<AnalysisItem>>(`/analyses/${analysisId}/sections/${sectionId}/copy-items`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  /** لیست آنالیزها برای انتخاب هدف کپی (همه به جز آنالیز جاری) */
  listForCopyTarget: (excludeAnalysisId: number, params?: { project_id?: number }) => {
    const q = new URLSearchParams();
    if (params?.project_id) q.set('project_id', String(params.project_id));
    const query = q.toString();
    return fetchApi<PaginatedResponse<AnalysisItem>>(`/analyses?per_page=100${query ? '&' + query : ''}`).then(
      (res) => ({ ...res, data: res.data.filter((a) => a.id !== excludeAnalysisId) })
    );
  },

  saveContractText: (id: number, contract_text: string) =>
    fetchApi<ApiResponse<AnalysisItem>>(`/analyses/${id}/save-contract-text`, {
      method: 'POST',
      body: JSON.stringify({ contract_text }),
    }),

  processAi: (id: number) =>
    fetchApi<{
      success: boolean;
      message?: string;
      analysis?: AiReviewData['last_review'] extends infer R ? R extends { analysis?: infer A } ? A : never : never;
      suggestions?: AiSuggestion[];
      missing_items?: AiMissingItem[];
      ai_review_data?: AiReviewData;
      data?: AnalysisItem;
    }>(`/analyses/${id}/process-ai`, { method: 'POST' }),

  aiChat: (id: number, message: string) =>
    fetchApi<{
      success: boolean;
      message?: string;
      reply?: string;
      suggestions?: AiSuggestion[];
      missing_items?: AiMissingItem[];
      applied_actions?: Array<{ type?: string; label?: string }>;
      actions?: unknown[];
      ai_review_data?: AiReviewData;
      data?: AnalysisItem;
    }>(`/analyses/${id}/ai-chat`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    }),

  applyAiMissingItems: (id: number, indexes: number[]) =>
    fetchApi<ApiResponse<AnalysisItem> & { applied?: unknown[]; ai_review_data?: AiReviewData }>(`/analyses/${id}/apply-ai-items`, {
      method: 'POST',
      body: JSON.stringify({ indexes }),
    }),

  acceptAiSuggestions: (id: number, indexes: number[]) =>
    fetchApi<ApiResponse<AnalysisItem> & { accepted?: unknown[]; ai_review_data?: AiReviewData }>(`/analyses/${id}/accept-ai-suggestions`, {
      method: 'POST',
      body: JSON.stringify({ indexes }),
    }),

  exportExcel: async (id: number, analysisName?: string) => {
    const url = `${API_BASE}/analyses/${id}/export-excel`;
    const r = await fetch(url, {
      credentials: 'omit',
      headers: getAuthHeaders(),
    });
    if (!r.ok) {
      if (r.status === 401 && typeof window !== 'undefined') {
        window.location.href = '/login';
        return;
      }
      throw new Error('خطا در دریافت فایل Excel');
    }
    const blob = await r.blob();
    const safeName = (analysisName ?? 'analysis').replace(/[^\p{L}\p{N}_\-]+/gu, '_');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${safeName}_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  },
};
